import { PROVIDER_SORT_OPTIONS } from "@src/constants/provider-search-sort.constants";
import type { Provider } from "@src/interfaces/provider.interface";
import ClientLogsService from "@src/services/client-logs.service";
import { orderVirtualOnlyProvidersLast } from "@src/utils/provider-modality-filter";
import { hasMixedSessionTypes } from "@src/utils/session-type-modality";
import type { FC, ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useApplicationContext } from "./ApplicationContext";

interface ProviderContextType {
  providers: Provider[];
  loading: boolean;
  totalProvidersCount: number;
  fetchProviders: (operation?: () => Promise<Provider[]>) => Promise<void>;
  sortBy: string;
  setSortBy: (sortBy: string) => void;
}

const ProviderContext = createContext<ProviderContextType | undefined>(undefined);

const normalizeSortValue = (value?: string): string => {
  return (value || "").trim().toLowerCase();
};

const compareProvidersByLastName = (firstProvider: Provider, secondProvider: Provider): number => {
  const firstProviderLastName = normalizeSortValue(firstProvider.lastName);
  const secondProviderLastName = normalizeSortValue(secondProvider.lastName);

  const lastNameComparison = firstProviderLastName.localeCompare(
    secondProviderLastName,
    undefined,
    {
      sensitivity: "base",
    },
  );

  if (lastNameComparison !== 0) {
    return lastNameComparison;
  }

  const firstNameComparison = normalizeSortValue(firstProvider.firstName).localeCompare(
    normalizeSortValue(secondProvider.firstName),
    undefined,
    {
      sensitivity: "base",
    },
  );

  if (firstNameComparison !== 0) {
    return firstNameComparison;
  }

  const fullNameComparison = firstProvider.fullName.localeCompare(
    secondProvider.fullName,
    undefined,
    {
      sensitivity: "base",
    },
  );

  if (fullNameComparison !== 0) {
    return fullNameComparison;
  }

  return firstProvider.providerId.localeCompare(secondProvider.providerId, undefined, {
    sensitivity: "base",
  });
};

export const ProviderListProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { data } = useApplicationContext();

  // Own providers as local state — initialized once from context cache.
  // This breaks the dependency chain that caused infinite re-fetching when
  // providerList was set to [] and fetchProviders was recreated each cycle.
  //
  // CONTRACT: data.providerList is only read here on initial mount (useState
  // initializer). After that, providers is exclusively updated via fetchProviders.
  // This is safe today because ProviderListProvider is mounted inside FindProvider,
  // and every caller that writes data.providerList (GetStartedForm, PatientInformation)
  // navigates away before writing, causing FindProvider/ProviderListProvider to
  // unmount and remount — reseeding state from the new data.providerList value.
  //
  // IMPORTANT: Do NOT update data.providerList from within a component that keeps
  // FindProvider mounted, as those changes will not be reflected here. Instead,
  // call fetchProviders directly with an operation that returns the updated list.
  const [providers, setProviders] = useState<Provider[]>(data.providerList || []);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalProvidersCount, setTotalProvidersCount] = useState<number>(
    data.providerList?.length || 0,
  );
  const [sortBy, setSortBy] = useState<string>(PROVIDER_SORT_OPTIONS.RECOMMENDED);

  // Keep a ref to the current userUuid so the stable fetchProviders callback
  // can still log the correct user identity without capturing data.userUuid
  // as a dependency (which would recreate the callback and re-trigger effects).
  const userUuidRef = useRef<string | undefined>(data.userUuid);
  useEffect(() => {
    userUuidRef.current = data.userUuid;
  }, [data.userUuid]);

  // Tracks the generation of the most-recent fetchProviders call.
  // When a newer call starts, the older call sees its generation is stale and
  // discards results / skips clearing the loading flag.
  const fetchGenRef = useRef(0);

  const fetchProviders = useCallback(
    async (operation?: () => Promise<Provider[]>) => {
      const thisGen = ++fetchGenRef.current;
      setLoading(true);
      try {
        if (operation) {
          const result = await operation();
          if (fetchGenRef.current !== thisGen) return; // superseded by a newer search
          setProviders(result);
          setTotalProvidersCount(result.length);
        } else {
          // Bootstrap path: providers already initialized from cache in useState,
          // just show the loader briefly so the UI doesn't flash.
          await new Promise((resolve) => {
            return setTimeout(resolve, 2000);
          });
        }
      } catch (error) {
        if ((error as { name?: string })?.name === "AbortError") {
          return; // Superseded by a newer search — silently ignore
        }
        if (fetchGenRef.current !== thisGen) return; // stale result from a superseded call
        ClientLogsService.logError(
          userUuidRef.current || "unknown",
          "ProviderListContext.fetchProviders",
          "Error fetching providers",
          error instanceof Error ? error : new Error("Unknown error"),
        );
      } finally {
        if (fetchGenRef.current === thisGen) {
          setLoading(false);
        }
      }
    },
    [], // stable — no external deps; setProviders/setLoading/setTotalProvidersCount are stable setState refs
  );

  // Memoize the sorted providers from local state
  const sortedProviders = useMemo(() => {
    const sortedBySelection = (() => {
      switch (sortBy) {
        case PROVIDER_SORT_OPTIONS.NAME_ASC:
          return [...providers].sort(compareProvidersByLastName);
        case PROVIDER_SORT_OPTIONS.NAME_DESC:
          return [...providers].sort((a, b) => {
            return compareProvidersByLastName(b, a);
          });
        case PROVIDER_SORT_OPTIONS.NEAREST_TO_FURTHEST:
          // Backend already returns providers sorted by distance for this sort_by value.
          return providers;
        default:
          return [...providers].sort((a, b) => {
            return b.availabilityScore - a.availabilityScore;
          });
      }
    })();

    // Virtual-only providers go last only for Distance sort with both session types selected.
    return hasMixedSessionTypes(data.sessionType) &&
      sortBy === PROVIDER_SORT_OPTIONS.NEAREST_TO_FURTHEST
      ? orderVirtualOnlyProvidersLast(sortedBySelection)
      : sortedBySelection;
  }, [data.sessionType, providers, sortBy]);

  const contextValue = useMemo(() => {
    return {
      providers: sortedProviders,
      loading,
      totalProvidersCount,
      fetchProviders,
      sortBy,
      setSortBy,
    };
  }, [sortedProviders, loading, totalProvidersCount, fetchProviders, sortBy]);

  return <ProviderContext.Provider value={contextValue}>{children}</ProviderContext.Provider>;
};

export const useProviderListContext = () => {
  const context = useContext(ProviderContext);
  if (!context)
    throw new Error("useProviderListContext must be used within a ProviderListProvider");
  return context;
};
